"use client";

// WO-GF-001-S26 · A3 — THE SITE-WIDE MOUNT for the one mountain selector (R1a `MountainSelectModal`).
//
// WHY THIS EXISTS. S25 built the R1a selector and mounted it in exactly ONE place — `BookingChrome`, the
// funnel pill. The mount audit (S26 · A2) then showed that every other entry point still opened one of
// the two OLD plain-list selectors, so the founder's walk hit the unchanged one and S25 was rejected.
// A selector that has to be mounted per entry point will always drift; this layer makes the mount a
// property of the SURFACE, and every entry point a plain request.
//
// THE CONTRACT (one rule, three former implementations — the F89 R1 precedent applied to chrome):
//   • any entry point opens the selector by dispatching `gf:mountain-select` on `document`;
//   • `detail.mode` carries the opening intent ("marketplace" | "transport" | "directory"), or is absent
//     for a neutral entry point (BOOK NOW, the change-mountain controls) → the mountain HUB;
//   • `detail.locate` (optional) names a mountain CODE the selector should pan its map to on open.
//     A code the geo endpoint does not serve locates nothing and opens the selector plain.
//   • the layer renders nothing at rest, so every at-rest pixel baseline is untouched.
//
// Native CustomEvent — deliberately NOT React state lifted through the tree. The entry points live in
// four different places (React headers, doc-scoped layers that wire CAPTURED markup, a portal, and —
// since 025 — the founder's own export, un-framed in this document), and a portal's click does not
// bubble through the React tree to the element it is portaled INTO. That is the precise bug this layer
// also fixes on the content pages (see `ContentHudLayer`).

import { useEffect, useState } from "react";
import MountainSelectModal from "./MountainSelectModal";
import type { MountainSelectMode } from "./MountainSelectModal";
// 025 · S2 — the request shape and its locate reader are pure and shared (see @/lib/mountain-select).
import { locateFromDetail } from "@/lib/mountain-select";
import type { MountainSelectRequest } from "@/lib/mountain-select";

export const MOUNTAIN_SELECT_EVENT = "gf:mountain-select";
export type { MountainSelectRequest };

/** Open the one selector from anywhere (React or plain DOM). `mode` = the opening intent, if any;
 *  `locate` = a mountain code to pan the map to once its pin exists. */
export function openMountainSelect(mode: MountainSelectMode = null, locate: string | null = null) {
  document.dispatchEvent(new CustomEvent<MountainSelectRequest>(MOUNTAIN_SELECT_EVENT, { detail: { mode, locate } }));
}

export default function MountainSelectLayer() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<MountainSelectMode>(null);
  // 025 · S2 — the locate request rides with its own nonce so the SAME mountain can be located twice
  // across two opens (the modal's focus is keyed on the nonce, exactly like its locate button's).
  const [locate, setLocate] = useState<{ code: string | null; nonce: number }>({ code: null, nonce: 0 });

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<MountainSelectRequest>).detail;
      setMode(detail?.mode ?? null);
      setLocate((l) => ({ code: locateFromDetail(detail), nonce: l.nonce + 1 }));
      setOpen(true);
    };
    document.addEventListener(MOUNTAIN_SELECT_EVENT, onOpen);
    return () => document.removeEventListener(MOUNTAIN_SELECT_EVENT, onOpen);
  }, []);

  return (
    <>
      <span data-mountain-select-layer hidden />
      <MountainSelectModal open={open} mode={mode} locate={locate.code} locateNonce={locate.nonce} onClose={() => setOpen(false)} />
    </>
  );
}
